import React, { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { InputBase, IconButton, makeStyles } from "@material-ui/core";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import {
  sendChatMessage,
  getConversations,
  getConversation,
  deleteConversation,
  renameConversation,
  clearChat,
} from "actions/chat-actions";
import { exportConversationPdfAPI } from "api/chat-api";
import { saveAs } from "file-saver";
import ChatSidebar from "./components/ChatSidebar";
import ChatHeader from "./components/ChatHeader";
import MessageList from "./components/MessageList";
import WelcomeScreen from "./components/WelcomeScreen";

const useStyles = makeStyles(() => ({
  root: {
    display: "flex",
    height: "100vh",
    background: "#ffffff",
    overflow: "hidden",
  },
  mainArea: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    background: "#F5F7FB",
    backgroundImage: "radial-gradient(circle, #c8d4e8 1px, transparent 1px)",
    backgroundSize: "24px 24px",
  },
  inputArea: {
    padding: "12px 24px 18px",
    borderTop: "1px solid #E8EBF0",
    background: "#F5F7FB",
    "@media (max-width: 768px)": {
      padding: "12px 16px 16px",
    },
  },
  inputContainer: {
    maxWidth: 720,
    margin: "0 auto",
    background: "#F7F8FA",
    border: "1px solid #e5e7eb",
    borderRadius: 16,
    padding: "4px 8px 8px 18px",
    transition: "box-shadow 0.2s, border-color 0.2s",
    "&:focus-within": {
      boxShadow: "0 0 0 3px rgba(37,99,235,0.08)",
      borderColor: "#93B4F5",
    },
  },
  inputBase: {
    flex: 1,
    fontSize: 14,
    lineHeight: 1.5,
    maxHeight: 150,
    overflowY: "auto",
    padding: "10px 0",
    fontFamily: "'IBM Plex Sans', sans-serif",
    "& input::placeholder, & textarea::placeholder": {
      color: "#A0A5B1",
      opacity: 1,
    },
  },
  deleteOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(0, 0, 0, 0.4)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 2000,
  },
  deleteDialog: {
    background: "#fff",
    borderRadius: 12,
    padding: "24px",
    maxWidth: 380,
    width: "90%",
    boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
  },
  deleteDialogTitle: {
    fontSize: 16,
    fontWeight: 600,
    color: "#111827",
    marginBottom: 8,
  },
  deleteDialogText: {
    fontSize: 14,
    color: "#6b7280",
    lineHeight: 1.5,
    marginBottom: 20,
  },
  deleteDialogActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 8,
  },
  deleteDialogCancel: {
    padding: "8px 16px",
    borderRadius: 8,
    border: "1px solid #e5e7eb",
    background: "#fff",
    color: "#374151",
    fontSize: 13,
    fontWeight: 500,
    cursor: "pointer",
    fontFamily: "inherit",
    "&:hover": {
      background: "#f9fafb",
    },
  },
  deleteDialogConfirm: {
    padding: "8px 16px",
    borderRadius: 8,
    border: "none",
    background: "#dc2626",
    color: "#fff",
    fontSize: 13,
    fontWeight: 500,
    cursor: "pointer",
    fontFamily: "inherit",
    "&:hover": {
      background: "#b91c1c",
    },
  },
  sendButton: {
    width: 34,
    height: 34,
    borderRadius: "10px",
    background: "linear-gradient(135deg, #1e3a5f, #2563eb)",
    color: "#fff",
    boxShadow: "0 1px 3px rgba(37,99,235,0.25)",
    transition: "box-shadow 0.15s",
    "&:hover": {
      boxShadow: "0 2px 6px rgba(37,99,235,0.35)",
    },
    "&:disabled": {
      background: "#ccc",
      color: "#fff",
      boxShadow: "none",
    },
  },
  inputRow: {
    display: "flex",
    alignItems: "center",
    paddingBottom: "6px",
  },
  agentToggleRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderTop: "1px solid #f3f4f6",
    paddingTop: "7px",
  },
  agentPill: {
    display: "flex",
    background: "#f1f5f9",
    borderRadius: "20px",
    padding: "3px",
    border: "1px solid #e2e8f0",
    gap: "2px",
  },
  agentOption: {
    padding: "3px 14px",
    borderRadius: "18px",
    fontSize: "12px",
    fontWeight: 600,
    fontFamily: "'DM Sans', sans-serif",
    cursor: "pointer",
    transition: "all 200ms ease-out",
    color: "#9ca3af",
    background: "transparent",
    border: "none",
    lineHeight: 1.4,
  },
  agentOptionActive: {
    background: "#fff",
    color: "#2563eb",
    boxShadow: "0 1px 4px rgba(37,99,235,0.15)",
  },
  disclaimer: {
    maxWidth: 720,
    margin: "10px auto 0",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    fontFamily: "'IBM Plex Sans', sans-serif",
    fontSize: 11,
    fontWeight: 400,
    color: "#9CA3AF",
    lineHeight: 1.5,
    letterSpacing: "0.25px",
    userSelect: "none",
  },
  disclaimerBrand: {
    fontWeight: 600,
    background: "linear-gradient(120deg, #1e3a5f 0%, #2563eb 60%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    letterSpacing: "0.2px",
  },
}));

export default function AIChat() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const messagesEndRef = useRef(null);
  const messagesAreaRef = useRef(null);
  const inputRef = useRef(null);

  const [inputValue, setInputValue] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [provider, setProvider] = useState("agent-1");

  const {
    conversations,
    activeConversationId,
    messages,
    sendingMessage,
    loadingConversations,
    loadingConversation,
  } = useSelector((state) => state.chatReducer);

  // Load conversations on mount
  useEffect(() => {
    dispatch(getConversations());
  }, [dispatch]);

  // Escape -> close sidebar on mobile
  useEffect(() => {
    const handleKeyboard = (e) => {
      if (e.key === "Escape" && window.innerWidth < 768) {
        setSidebarOpen(false);
      }
    };
    document.addEventListener("keydown", handleKeyboard);
    return () => document.removeEventListener("keydown", handleKeyboard);
  }, []);

  // Load Google Fonts
  useEffect(function () {
    var fontUrl = "https://fonts.googleapis.com/css2?family=DM+Sans:wght@500;600;700&family=IBM+Plex+Mono:wght@400;500&family=IBM+Plex+Sans:wght@400;500;600&display=swap";
    if (!document.querySelector('link[href="' + fontUrl + '"]')) {
      var link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = fontUrl;
      document.head.appendChild(link);
    }
  }, []);

  const handleSend = useCallback(
    (text) => {
      const question = (text || inputValue).trim();
      if (!question || sendingMessage) return;
      if (!navigator.onLine) return;
      setInputValue("");
      dispatch(sendChatMessage(question, activeConversationId, provider));
    },
    [inputValue, sendingMessage, activeConversationId, provider, dispatch]
  );

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleNewChat = () => {
    dispatch(clearChat());
    setProvider("agent-1");
  };

  const handleSelectConversation = (id) => {
    dispatch(getConversation(id));
  };

  const handleDeleteConversation = (e, id) => {
    e.stopPropagation();
    setDeleteConfirmId(id);
  };

  const handleConfirmDelete = () => {
    if (deleteConfirmId) {
      dispatch(deleteConversation(deleteConfirmId));
    }
    setDeleteConfirmId(null);
  };

  const handleCancelDelete = () => {
    setDeleteConfirmId(null);
  };

  const handleRenameConversation = (id, title) => {
    dispatch(renameConversation(id, title));
  };

  const handleExportPdf = async () => {
    if (!activeConversationId) return;
    try {
      const blob = await exportConversationPdfAPI(activeConversationId);
      if (blob instanceof Blob) {
        saveAs(blob, `chat-export-${activeConversationId.slice(0, 8)}.pdf`);
      }
    } catch (err) {
      // Silent fail for now
    }
  };

  const handleBackToDashboard = () => {
    history.push("/projects/financial-dashboard");
  };

  const userName = localStorage.getItem("Login_User_Name") || "You";

  return (
    <div className={classes.root}>
      <ChatSidebar
        open={sidebarOpen}
        conversations={conversations}
        activeConversationId={activeConversationId}
        loadingConversations={loadingConversations}
        onNewChat={handleNewChat}
        onSelectConversation={handleSelectConversation}
        onDeleteConversation={handleDeleteConversation}
        onRenameConversation={handleRenameConversation}
        onClose={() => setSidebarOpen(false)}
      />
      <div className={classes.mainArea}>
        <ChatHeader
          sidebarOpen={sidebarOpen}
          onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
          activeConversationId={activeConversationId}
          onExportPdf={handleExportPdf}
          onBackToDashboard={handleBackToDashboard}
          provider={provider}
        />
        {messages.length === 0 && !loadingConversation ? (
          <WelcomeScreen onSendSuggestion={handleSend} />
        ) : (
          <MessageList
            messages={messages}
            sendingMessage={sendingMessage}
            loadingConversation={loadingConversation}
            onFollowUpSelect={handleSend}
            userName={userName}
            messagesAreaRef={messagesAreaRef}
            messagesEndRef={messagesEndRef}
          />
        )}
        <div className={classes.inputArea}>
          <div className={classes.inputContainer}>
            <div className={classes.inputRow}>
              <InputBase
                inputRef={inputRef}
                className={classes.inputBase}
                placeholder="Ask about your financial data..."
                multiline
                maxRows={4}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={sendingMessage}
              />
              <IconButton
                className={classes.sendButton}
                onClick={() => handleSend()}
                disabled={!inputValue.trim() || sendingMessage}
                size="small"
              >
                <ArrowUpwardIcon style={{ fontSize: 18 }} />
              </IconButton>
            </div>
            <div className={classes.agentToggleRow}>
              <div className={classes.agentPill}>
                <button
                  className={
                    classes.agentOption +
                    (provider === "agent-1" ? " " + classes.agentOptionActive : "")
                  }
                  onClick={() => setProvider("agent-1")}
                >
                  ● Scout
                </button>
                <button
                  className={
                    classes.agentOption +
                    (provider === "agent-2" ? " " + classes.agentOptionActive : "")
                  }
                  onClick={() => setProvider("agent-2")}
                >
                  Strategist
                </button>
              </div>
            </div>
          </div>
          <div className={classes.disclaimer}>
            <span>
              <span className={classes.disclaimerBrand}>NQ Assist</span>
              {" is an AI assistant and may make mistakes. Please verify important data."}
            </span>
          </div>
        </div>
      </div>
      {deleteConfirmId && (
        <div className={classes.deleteOverlay}>
          <div className={classes.deleteDialog}>
            <div className={classes.deleteDialogTitle}>Delete conversation?</div>
            <div className={classes.deleteDialogText}>
              This will permanently delete this conversation and all its messages.
            </div>
            <div className={classes.deleteDialogActions}>
              <button className={classes.deleteDialogCancel} onClick={handleCancelDelete}>
                Cancel
              </button>
              <button className={classes.deleteDialogConfirm} onClick={handleConfirmDelete}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
